// Shared derivation helpers for the payment-methods UI. Used by both the
// client-portal MyBillingPage and the staff ClientBillingThresholdPage so
// the two views agree on what counts as expiring / expired / pending /
// failing. Keep these pure — render layer plugs them in.

const EXPIRING_SOON_DAYS = 60;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Classify a card's expiration into one of:
 *   'expired'      — already past exp month
 *   'expiring'     — within EXPIRING_SOON_DAYS
 *   'ok'           — well in the future
 *   null           — not a card / missing fields
 */
export function cardExpirationStatus(method, now = new Date()) {
  if (!method || method.type !== 'card') return null;
  const month = Number(method.exp_month);
  const year = Number(method.exp_year);
  if (!month || !year) return null;
  // Cards expire at the end of their exp month — give them through the
  // last day. Stripe treats them the same way internally.
  const expEnd = new Date(year, month, 0, 23, 59, 59);
  if (expEnd < now) return 'expired';
  const daysLeft = (expEnd - now) / MS_PER_DAY;
  if (daysLeft <= EXPIRING_SOON_DAYS) return 'expiring';
  return 'ok';
}

export function formatCardExpiration(method) {
  if (!method || method.type !== 'card') return null;
  const month = Number(method.exp_month);
  const year = Number(method.exp_year);
  if (!month || !year) return null;
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `Exp ${mm}/${yy}`;
}

/** Returns true if this method is a bank still awaiting microdeposit verification. */
export function isUnverifiedBank(method) {
  return method?.type === 'us_bank_account' && method?.is_verified === false;
}

/**
 * Reduce a method to a single "row status" used to colour the chip group.
 * Order of precedence matters — what's surfaced is the most urgent issue.
 *   pending_verification > expired > last_charge_failed > expiring > ok
 */
export function methodRowStatus(method) {
  if (isUnverifiedBank(method)) return 'pending_verification';
  if (cardExpirationStatus(method) === 'expired') return 'expired';
  if (method?.last_charge && method.last_charge.succeeded === false)
    return 'last_charge_failed';
  if (cardExpirationStatus(method) === 'expiring') return 'expiring';
  return 'ok';
}

/**
 * Build the fleet of issues across a payment_methods response. Drives the
 * top-of-card health banner.
 *
 *   {
 *     anyIssues: bool,
 *     pendingBanks: [method, …],
 *     expired: [method, …],
 *     expiring: [method, …],
 *     failingMethods: [method, …],
 *     hasPrimary: bool,
 *     onlyMethodIsUnverified: bool,
 *   }
 */
export function summarizePaymentHealth(paymentMethods) {
  const methods = paymentMethods?.methods || [];
  const pendingBanks = methods.filter(isUnverifiedBank);
  const expired = methods.filter(
    (m) => cardExpirationStatus(m) === 'expired',
  );
  const expiring = methods.filter(
    (m) => cardExpirationStatus(m) === 'expiring',
  );
  const failingMethods = methods.filter(
    (m) => m.last_charge && m.last_charge.succeeded === false,
  );
  const hasPrimary = methods.some((m) => m.is_default);
  const onlyMethodIsUnverified =
    methods.length === 1 && isUnverifiedBank(methods[0]);

  return {
    anyIssues:
      pendingBanks.length > 0 ||
      expired.length > 0 ||
      expiring.length > 0 ||
      failingMethods.length > 0 ||
      (methods.length > 0 && !hasPrimary),
    pendingBanks,
    expired,
    expiring,
    failingMethods,
    hasPrimary,
    onlyMethodIsUnverified,
  };
}

/** Human-readable "Last charge: <succeeded|declined>, <relative time>". */
export function formatLastChargeLine(method, now = new Date()) {
  const lc = method?.last_charge;
  if (!lc) return null;
  const created = Number(lc.created || 0);
  if (!created) return null;
  const when = new Date(created * 1000);
  const minutesAgo = Math.max(0, Math.round((now - when) / 60000));
  const hoursAgo = Math.round(minutesAgo / 60);
  const daysAgo = Math.round(hoursAgo / 24);
  let rel;
  if (minutesAgo < 60) rel = `${minutesAgo}m ago`;
  else if (hoursAgo < 48) rel = `${hoursAgo}h ago`;
  else rel = `${daysAgo}d ago`;
  const verb = lc.succeeded ? 'Succeeded' : 'Declined';
  return `${verb} • ${rel}`;
}
